import axios from 'axios';
import { useMutation } from '@tanstack/react-query';

import { createMenuManagementAxiosInstance } from 'createAxiosInstance';
import { logException } from 'helpers/loggers';

type EnhancePhotoParams = {
  imageUrl: string;
};

type EnhanceApiResponse = {
  image_data: string;
  media_type: string;
};

type EnhanceApiErrorResponse = {
  error: string;
  message: string;
  details: unknown;
};

export function getEnhancePhotoErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError<EnhanceApiErrorResponse>(error)) return undefined;
  return error.response?.data?.error;
}

export async function imageUrlToBase64(url: string): Promise<{ data: string; mediaType: string }> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
  const blob = await response.blob();
  const mediaType = blob.type || 'image/jpeg';
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // loadend also fires after a failed read (reader.error set); onerror already
      // rejected in that case, so skip parsing reader.result (null on failure).
      if (reader.error) return;
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve({ data: base64, mediaType });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export const useEnhancePhoto = (companyId: string | undefined) => {
  const mutation = useMutation<string, Error, EnhancePhotoParams>({
    mutationFn: async ({ imageUrl }) => {
      const { data: imageData, mediaType } = await imageUrlToBase64(imageUrl);
      const axiosInstance = await createMenuManagementAxiosInstance();
      const response = await axiosInstance.post<EnhanceApiResponse>(
        `company/${companyId}/photo/enhance`,
        {
          image_data: imageData,
          media_type: mediaType,
        },
      );
      return `data:${response.data.media_type};base64,${response.data.image_data}`;
    },
  });

  const enhancePhoto = async (params: EnhancePhotoParams) => {
    if (!companyId) throw new Error('companyId is required');
    try {
      const result = await mutation.mutateAsync(params);
      return result;
    } catch (error) {
      logException({
        message: 'Failed to enhance photo',
        error,
        context: null,
        tags: null,
      });
      throw error;
    }
  };

  return { enhancePhoto };
};
